import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AGGREGATIONS,
  CAPACITY_BASES,
  CAPACITY_BASIS_LABELS,
  CAPACITY_BASIS_TIPS,
  coercePerfSettings,
  DEFAULT_CAPACITY_BASIS,
  lookbackClampNotice,
} from '../../lib/perfSettings';
import type { PerfAggregation, PerfSettings } from '../../types';
import { Icon } from '../shared/Icon';
import { ContextOverlay } from './overlay';

/**
 * How Performance draws its figures, kept out of the context (spec 7.2):
 * the chart's breakdown (days … years) and the capacity occupancy is
 * measured against. Live has the capacity basis only. A change applies at
 * once: it changes no count.
 */
export function DisplaySettings({
  settings,
  onChange,
  breakdown,
}: {
  settings: PerfSettings;
  onChange: (next: PerfSettings) => void;
  /** Performance shows the chart breakdown; Live only the capacity basis. */
  breakdown: boolean;
}) {
  const { t } = useTranslation('chrome');
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  // Some breakdowns can't hold the period (days over years): said, not done quietly.
  const [clamp, setClamp] = useState<string | null>(null);
  const basis = settings.capacityBasis ?? DEFAULT_CAPACITY_BASIS;
  const value = breakdown
    ? `${t(`aggregation.${settings.aggregation}Plural`)} · ${CAPACITY_BASIS_LABELS[basis]}`
    : CAPACITY_BASIS_LABELS[basis];
  const titleId = 'cx-display-title';
  const set = (next: Partial<PerfSettings>) => {
    const requested = { ...settings, ...next };
    const applied = coercePerfSettings(requested);
    setClamp(lookbackClampNotice(requested, applied));
    onChange(applied);
  };
  return (
    <>
      <button
        ref={ref}
        type="button"
        className="tb-btn cx-display-btn"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={t('context.display.aria', { value })}
        onClick={() => {
          setClamp(null);
          setOpen(v => !v);
        }}
      >
        <Icon name="sliders" size={13} />
        <span className="cx-display-lab">{t('context.display.button')}</span>
        <span className="cx-display-val">{value}</span>
      </button>
      <ContextOverlay
        open={open}
        anchorRef={ref}
        onClose={() => setOpen(false)}
        width={340}
        align="end"
        className="cx-menu"
        labelledBy={titleId}
      >
        <div className="cx-menu-head">
          <h2 className="cx-menu-title" id={titleId}>
            {t('context.display.title')}
          </h2>
          <button type="button" className="cx-icon-btn" onClick={() => setOpen(false)} aria-label={t('context.panel.close')}>
            <Icon name="close" size={14} />
          </button>
        </div>
        <p className="cx-menu-note">{breakdown ? t('context.display.note') : t('context.display.liveNote')}</p>
        {breakdown ? (
          <fieldset className="cx-group">
            <legend className="cx-group-head">
              <span>{t('context.display.breakdown')}</span>
            </legend>
            <div className="cx-seg is-wrap">
              {AGGREGATIONS.map((agg: PerfAggregation) => (
                <label key={agg} className={`cx-seg-opt${settings.aggregation === agg ? ' is-on' : ''}`}>
                  <input
                    type="radio"
                    name="cx-breakdown"
                    checked={settings.aggregation === agg}
                    onChange={() => set({ aggregation: agg })}
                  />
                  {t(`aggregation.${agg}Plural`)}
                </label>
              ))}
            </div>
          </fieldset>
        ) : null}
        {clamp ? (
          <p className="cx-note" role="status">
            {clamp}
          </p>
        ) : null}
        <fieldset className="cx-group">
          <legend className="cx-group-head">
            <span>{t('context.display.capacity')}</span>
          </legend>
          <div className="cx-list">
            {CAPACITY_BASES.map(b => (
              <label key={b} className={`cx-opt${basis === b ? ' is-on' : ''}`}>
                <input type="radio" name="cx-capacity" checked={basis === b} onChange={() => set({ capacityBasis: b })} />
                <span className="cx-opt-main">
                  <span className="cx-opt-name">{CAPACITY_BASIS_LABELS[b]}</span>
                  <span className="cx-opt-sub">{CAPACITY_BASIS_TIPS[b]}</span>
                </span>
              </label>
            ))}
          </div>
        </fieldset>
      </ContextOverlay>
    </>
  );
}
